<template>
  <LayoutPage>
    <!-- Header Section -->
    <div class="mt-2 flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Tag Management</h1>
        <p class="text-sm text-gray-600 mt-1">Create and manage tags for organizing your campaigns and content</p>
      </div>
      <div class="flex items-center space-x-3">
        <el-button @click="openCreateModal" type="primary" class="uppercase">
          <i class="el-icon-plus mr-2"></i>
          Create Tag
        </el-button>
      </div>
    </div>

    <!-- Tabs -->
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Active Tags" name="active"> </el-tab-pane>
      <el-tab-pane label="Archived Tags" name="archived"> </el-tab-pane>
    </el-tabs>

    <!-- Tags Table -->
    <div class="bg-white rounded-lg border overflow-hidden">
      <template v-if="loading">
        <div class="p-8 text-center">
          <Spinner />
          <p class="text-gray-500 mt-2">Loading tags...</p>
        </div>
      </template>

      <template v-else-if="loadedError">
        <div class="p-8 text-center">
          <i class="el-icon-warning text-6xl text-red-300 mb-4"></i>
          <h3 class="text-lg font-medium text-gray-900 mb-2">Failed to load tags</h3>
          <p class="text-gray-500 mb-4">There was an error loading your tags</p>
          <el-button @click="loadTags" type="primary">Try Again</el-button>
        </div>
      </template>

      <template v-else-if="loadedOk && currentTags.length > 0">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style="width: 20%"
                >
                  Tag
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th
                  scope="col"
                  class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  style="width: 120px"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="tag in currentTags" :key="tag.id" class="hover:bg-gray-50 table-row">
                <td class="px-6 py-2 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-4 h-4 rounded-full mr-3 flex-shrink-0" :style="{ backgroundColor: tag.color }"></div>
                    <div class="text-sm font-medium text-gray-900">{{ tag.name }}</div>
                  </div>
                </td>
                <td class="px-6 py-2">
                  <div class="text-sm text-gray-600" :title="tag.description">
                    {{ tag.description || 'No description' }}
                  </div>
                </td>
                <td class="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                  <el-dropdown trigger="click" @command="handleTagAction(tag, $event)">
                    <el-button type="text" size="small">
                      <i class="el-icon-more"></i>
                    </el-button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item v-if="activeTab === 'active'" command="edit" icon="el-icon-edit"
                          >Edit Tag</el-dropdown-item
                        >
                        <el-dropdown-item v-if="activeTab === 'active'" command="delete" icon="el-icon-delete"
                          >Delete Tag</el-dropdown-item
                        >
                        <el-dropdown-item v-if="activeTab === 'archived'" command="restore" icon="el-icon-refresh-left">
                          Restore Tag
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else-if="loadedOk && currentTags.length === 0">
        <div class="p-8 text-center">
          <i class="el-icon-collection-tag text-6xl text-gray-300 mb-4"></i>
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ activeTab === 'active' ? 'No active tags yet' : 'No archived tags' }}
          </h3>
          <p class="text-gray-500 mb-4">
            {{ activeTab === 'active' ? 'Create your first tag to get started' : 'Tags that are deleted will appear here' }}
          </p>
          <el-button v-if="activeTab === 'active'" @click="openCreateModal" type="primary">
            <i class="el-icon-plus mr-2"></i>
            Create Tag
          </el-button>
        </div>
      </template>
    </div>

    <!-- Create/Edit Tag Modal -->
    <el-dialog
      :title="editingTag ? 'Edit Tag' : 'Create Tag'"
      v-model="showCreateModal"
      width="520px"
      :close-on-click-modal="false"
    >
      <div class="px-8 py-4">
        <el-form :model="tagForm" :rules="tagRules" ref="tagFormRef" label-width="100px" label-position="left">
          <el-form-item label="Name" prop="name" class="mb-6">
            <el-input
              v-model="tagForm.name"
              placeholder="Enter tag name"
              maxlength="100"
              @input="validateTagNameInput"
            ></el-input>
          </el-form-item>

          <el-form-item label="Description" prop="description" class="mb-6">
            <el-input
              v-model="tagForm.description"
              type="textarea"
              :rows="4"
              placeholder="Enter tag description (optional)"
              maxlength="500"
              resize="none"
            ></el-input>
          </el-form-item>

          <el-form-item label="Color" prop="color" class="mb-2">
            <div class="flex items-center space-x-4">
              <!-- Color Preview Circle -->
              <div
                class="w-12 h-12 rounded-full border-2 border-gray-300 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                :style="{ backgroundColor: tagForm.color }"
                @click="showColorPicker = !showColorPicker"
              ></div>

              <!-- Color Input -->
              <div class="flex-1">
                <el-input v-model="tagForm.color" placeholder="#3B82F6" @input="updateColor">
                  <template #prepend>
                    <span class="text-gray-500">#</span>
                  </template>
                </el-input>
              </div>

              <!-- Color Picker Button -->
              <el-button @click="showColorPicker = !showColorPicker" type="text">
                <i class="el-icon-edit-outline"></i>
              </el-button>
            </div>

            <!-- Color Palette -->
            <div v-if="showColorPicker" class="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div class="grid grid-cols-8 gap-2 mb-4">
                <div
                  v-for="color in predefinedColors"
                  :key="color"
                  class="w-8 h-8 rounded cursor-pointer border-2 border-gray-300 hover:border-gray-400 transition-colors"
                  :class="{ 'ring-2 ring-blue-500 ring-offset-2': tagForm.color === color }"
                  :style="{ backgroundColor: color }"
                  @click="selectColor(color)"
                ></div>
              </div>

              <!-- Custom Color Picker -->
              <div class="flex items-center space-x-3">
                <span class="text-sm text-gray-600">Custom:</span>
                <input
                  type="color"
                  v-model="tagForm.color"
                  class="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  @input="updateColor"
                />
                <span class="text-sm text-gray-500">{{ tagForm.color.toUpperCase() }}</span>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="flex justify-end space-x-3 pt-2">
          <el-button @click="showCreateModal = false">
            Cancel
          </el-button>
          <el-button type="primary" @click="saveTag" :loading="saving" size="small">
            {{ editingTag ? 'Update' : 'Create' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </LayoutPage>
</template>

<script>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { fetchTagList, fetchArchivedTagList, createTag, updateTag, restoreTag, deleteTag } from '@/services/tagService'

export default {
  name: 'TagManagementPage',
  setup() {
    const tags = ref([])
    const archivedTags = ref([])
    const activeTab = ref('active')
    const loading = ref(false)
    const loadedOk = ref(false)
    const loadedError = ref(false)
    const saving = ref(false)
    const showCreateModal = ref(false)
    const editingTag = ref(null)
    const tagFormRef = ref(null)
    const showColorPicker = ref(false)

    // Predefined color palette
    const predefinedColors = ref([
      '#3B82F6',
      '#EF4444',
      '#10B981',
      '#F59E0B',
      '#8B5CF6',
      '#EC4899',
      '#06B6D4',
      '#84CC16',
      '#F97316',
      '#6366F1',
      '#14B8A6',
      '#EAB308',
      '#DC2626',
      '#059669',
      '#D97706',
      '#7C3AED',
      '#BE185D',
      '#0891B2',
      '#65A30D',
      '#EA580C',
      '#4F46E5',
      '#0D9488',
      '#CA8A04',
      '#B91C1C',
      '#047857',
      '#C2410C',
      '#3730A3',
      '#0F766E',
      '#A16207',
      '#991B1B',
      '#065F46',
      '#9A3412'
    ])

    // Computed property to get current tags based on active tab
    const currentTags = computed(() => {
      return activeTab.value === 'active' ? tags.value : archivedTags.value
    })

    const tagForm = reactive({
      name: '',
      description: '',
      color: '#3B82F6'
    })

    const validateTagName = (rule, value, callback) => {
      // Basic validation only - uniqueness is handled by backend
      if (!value || value.trim() === '') {
        callback()
        return
      }

      // Basic format validation
      const trimmedName = value.trim()
      if (trimmedName.length > 100) {
        callback(new Error('Tag name must not exceed 100 characters'))
        return
      }

      callback()
    }

    const validateTagNameInput = () => {
      // Trigger validation on input change
      if (tagFormRef.value) {
        tagFormRef.value.validateField('name')
      }
    }

    const tagRules = {
      name: [
        { required: true, message: 'Tag name is required', trigger: 'blur' },
        { min: 1, max: 100, message: 'Tag name must be between 1 and 100 characters', trigger: 'blur' },
        { validator: validateTagName, trigger: 'blur' }
      ],
      description: [{ max: 500, message: 'Description must not exceed 500 characters', trigger: 'blur' }]
    }

    const loadTags = async () => {
      loading.value = true
      loadedError.value = false

      try {
        const response = await fetchTagList()
        tags.value = response.data || []
        loadedOk.value = true
      } catch (error) {
        console.error('Error loading tags:', error)
        loadedError.value = true
        ElMessage.error('Failed to load tags')
      } finally {
        loading.value = false
      }
    }

    const loadArchivedTags = async () => {
      try {
        console.log('Starting to load archived tags...')
        const response = await fetchArchivedTagList()
        console.log('Archived tags response:', response)
        console.log('Response data:', response.data)
        console.log('Response status:', response.status)

        // Handle both response.data and direct response
        const tags = response.data || response || []
        console.log('Processed tags:', tags)
        archivedTags.value = tags
        console.log('Archived tags loaded:', archivedTags.value)
        console.log('Archived tags count:', archivedTags.value.length)
      } catch (error) {
        console.error('Error loading archived tags:', error)
        console.error('Error details:', error.response)
        ElMessage.error('Failed to load archived tags')
      }
    }

    // Watch for tab changes
    watch(activeTab, async (newTab, oldTab) => {
      console.log('Tab changed from', oldTab, 'to', newTab)
      if (newTab === 'active') {
        console.log('Loading active tags...')
        await loadTags()
      } else if (newTab === 'archived') {
        console.log('Loading archived tags...')
        await loadArchivedTags()
      }
    })

    // Watch for modal close to ensure form is reset
    watch(showCreateModal, async isOpen => {
      if (!isOpen) {
        // Modal is closing, reset form after a small delay to ensure DOM updates
        await nextTick()
        await resetForm()
      }
    })

    const saveTag = async () => {
      if (!tagFormRef.value) return

      const valid = await tagFormRef.value.validate()
      if (!valid) return

      saving.value = true

      try {
        if (editingTag.value) {
          // Update existing tag
          await updateTag(editingTag.value.id, {
            name: tagForm.name,
            description: tagForm.description,
            color: tagForm.color
          })
          ElMessage.success('Tag updated successfully')
        } else {
          // Create new tag
          await createTag({
            name: tagForm.name,
            description: tagForm.description,
            color: tagForm.color
          })
          ElMessage.success('Tag created successfully')
        }

        // Close modal - the watcher will handle form reset
        showCreateModal.value = false
        // Refresh both active and archived tags
        await loadTags()
        await loadArchivedTags()
      } catch (error) {
        console.error('Error saving tag:', error)
        if (error.response?.status === 409) {
          // Use the specific error message from the backend
          const errorMessage = error.response?.data?.error || 'A tag with this name already exists'
          ElMessage.error(errorMessage)
        } else if (error.response?.status === 400) {
          // Handle validation errors from backend
          const errorMessage = error.response?.data?.error || 'Invalid input data'
          ElMessage.error(errorMessage)
        } else {
          ElMessage.error('Failed to save tag')
        }
      } finally {
        saving.value = false
      }
    }

    const handleTagAction = async (tag, command) => {
      if (command === 'edit') {
        editingTag.value = tag
        tagForm.name = tag.name
        tagForm.description = tag.description || ''
        tagForm.color = tag.color
        showCreateModal.value = true
      } else if (command === 'delete') {
        try {
          await ElMessageBox.confirm(`Are you sure you want to delete the tag "${tag.name}"?`, 'Delete Tag', {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })

          await deleteTag(tag.id)
          ElMessage.success('Tag deleted successfully')
          // Refresh both active and archived tags
          await loadTags()
          await loadArchivedTags()
        } catch (error) {
          if (error !== 'cancel') {
            console.error('Error deleting tag:', error)
            ElMessage.error('Failed to delete tag')
          }
        }
      } else if (command === 'restore') {
        try {
          await ElMessageBox.confirm(`Are you sure you want to restore the tag "${tag.name}"?`, 'Restore Tag', {
            confirmButtonText: 'Restore',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })

          await restoreTag(tag.id)
          ElMessage.success('Tag restored successfully')
          // Refresh both active and archived tags
          await loadTags()
          await loadArchivedTags()
        } catch (error) {
          if (error !== 'cancel') {
            console.error('Error restoring tag:', error)
            if (error.response?.status === 409) {
              // Use the specific error message from the backend
              const errorMessage = error.response?.data?.error || 'A tag with this name already exists'
              ElMessage.error(errorMessage)
            } else if (error.response?.status === 400) {
              // Handle validation errors from backend
              const errorMessage = error.response?.data?.error || 'Invalid input data'
              ElMessage.error(errorMessage)
            } else {
              ElMessage.error('Failed to restore tag')
            }
          }
        }
      }
    }

    const resetForm = async () => {
      editingTag.value = null
      showColorPicker.value = false

      // Reset form fields using Object.assign to ensure reactive updates
      Object.assign(tagForm, {
        name: '',
        description: '',
        color: '#3B82F6'
      })

      // Clear form validation state - wait for next tick to ensure form ref is available
      await nextTick()
      if (tagFormRef.value) {
        tagFormRef.value.resetFields()
        tagFormRef.value.clearValidate()
      }
    }

    const openCreateModal = async () => {
      await resetForm()
      showCreateModal.value = true
    }

    const selectColor = color => {
      tagForm.color = color
      showColorPicker.value = false
    }

    const updateColor = () => {
      // Validate hex color format
      if (tagForm.color && !tagForm.color.startsWith('#')) {
        tagForm.color = '#' + tagForm.color
      }
    }

    onMounted(() => {
      loadTags()
    })

    return {
      tags,
      archivedTags,
      activeTab,
      currentTags,
      loading,
      loadedOk,
      loadedError,
      saving,
      showCreateModal,
      editingTag,
      tagFormRef,
      tagForm,
      tagRules,
      showColorPicker,
      predefinedColors,
      loadTags,
      loadArchivedTags,
      saveTag,
      handleTagAction,
      resetForm,
      openCreateModal,
      selectColor,
      updateColor,
      validateTagNameInput
    }
  }
}
</script>

<style scoped>
/* Table improvements */
.table-row:hover {
  background-color: #f9fafb;
  transition: background-color 0.2s;
}

/* Modal improvements */
.el-dialog {
  border-radius: 8px;
}

.el-dialog__header {
  padding: 16px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.el-dialog__body {
  padding: 24px;
}

.el-dialog__footer {
  padding: 12px 24px 16px;
  border-top: 1px solid #f0f0f0;
}

.el-form-item {
  margin-bottom: 24px;
}

.el-form-item:last-child {
  margin-bottom: 0;
}

.el-input__wrapper {
  border-radius: 6px;
}

.el-textarea__inner {
  border-radius: 6px;
}

/* Color picker styling */
input[type='color'] {
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
}

input[type='color']:hover {
  border-color: #c0c4cc;
}

input[type='color']:focus {
  border-color: #409eff;
  outline: none;
}

/* Color preview circle */
.color-preview {
  transition: all 0.2s ease;
}

.color-preview:hover {
  transform: scale(1.05);
}

/* Color palette grid */
.color-palette {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
}

.color-option {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
}

.color-option:hover {
  border-color: #9ca3af;
  transform: scale(1.1);
}

.color-option.selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Color picker panel */
.color-picker-panel {
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 16px;
  margin-top: 16px;
}
</style>
